import React, { useState, useEffect, useRef } from 'react';
import { RecordVideos } from 'components/Video/RecordVideos';
import { TournamentLink } from 'views/Tournament/TournamentLink';
import { GameScoreTabs } from 'views/PvP/game/GameScoreTabs';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {KingoftheHillGameData, KingoftheHillGameQuery, KingoftheHillGameVars} from '@pvplevelup/core/dist/queries/game/kingofthehill';
import { useQuery } from '@apollo/client';
import { Typography, Select, MenuItem, colors, useMediaQuery } from '@material-ui/core';
import { ZoneInformation, IZone, IMap, ControlPointType, Point } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { IPlayerLocation } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import MapRenderer, { MapDataTypes } from 'components/Map/MapRenderer';
import { MapIcons, MapThumbnailIcons } from 'utilities/map_icons';
import ReactResizeDetector from 'react-resize-detector';
import { GameHeader } from '../PvP/game/GameHeader';
import { WinnerBanner } from '../PvP/game/WinnerBanner';
import { GameScore, getTeamScores } from '../PvP/game/GameScore';
import { DeathBreakdown } from '../PvP/game/DeathBreakdown';
import { PlayerScore } from '../PvP/game/PvPGame';
import { classImages } from 'utilities/ClassImages';
import { RouteComponentProps } from 'react-router-dom';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { TeamColors } from 'utilities/team_colors';
import { breakPoint } from 'components/Character/CharacterStats';
import { blackColor } from 'assets/jss/material-dashboard-react';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';



const useStyles = makeStyles(theme => ({
  root:{
    flexGrow:1
  },
  gameMode: {
    color: 'grey'
  },
  hr: {
    border: '1px solid #424242',
    borderBottom: 0
  },
  mainContent: {
    flex: '1 1 auto',
    overflowY: 'auto'
  },
  footer: {
    height: '40px'
  },
  main: {
    height:'100%'
  },
  noGrow: {
    flex: '0 0 auto'
  },
  background: {
    position: "absolute",
    zIndex: -1,
    height: "100%",
    width: "100%",
    display: "block",
    top: "0",
    left: "0",
    backgroundSize: "cover",
    backgroundPosition: "center center",
    "&:after": {
      position: "absolute",
      zIndex: "3",
      width: "100%",
      height: "100%",
      content: '""',
      display: "block",
      background: blackColor,
      opacity: ".65"
    }
  }
}));

interface Props{
}

interface RouteParams {
  id?: string;
}


export const KingoftheHillSummary: React.FC<Props & RouteComponentProps<RouteParams>> = (props) => {
  const classes = useStyles();

  let [currentLocale, setCurrentLocale] = useState<string | undefined>("");
  let [imageWidth, setImageWidth] = useState(5);
  let [containterWidth, setContainerWidth] = useState(0);
  const imageWidthRef = useRef(imageWidth);
  imageWidthRef.current = imageWidth;
  const [imageResizeTimeout, setImageResizeTimeout] = useState<NodeJS.Timeout | undefined>();
  let [isResizing, setIsResizing] = useState(false);

  const [zone, setZone] = useState<IZone | null>(null);
  const [map, setMap] = useState<IMap | null>(null);
  let [ position, setPosition ] = useState<IPlayerLocation | null>(null);
  const positionRef = useRef(position);
  positionRef.current = position;

  let [teamScores, setTeamScores] = useState<Array<PlayerScore[]>>([]);
  let [hill, setHill] = useState<Point | undefined>(undefined);
  const hillRef = useRef(hill);
  hillRef.current = hill;

  let [controlling, setControlling] = useState<number | undefined>(undefined);
  let [pointColor, setPointColor] = useState<string>(colors.common.white);

  const { subscribeToMore, data, error, refetch } = useQuery<KingoftheHillGameData,KingoftheHillGameVars>(
    KingoftheHillGameQuery,
    { variables: {id:props.match.params.id},fetchPolicy:'network-only' }
  );

  useEffect(()=>{
    let z = ZoneInformation[data?.kingofthehill_game?.zone || ""] || null;
    let m = (z) ? z.mapInfo[data?.kingofthehill_game?.map || ""] : null;
    setZone(z);
    setMap(m);
  },[data?.kingofthehill_game?._id])


  const updateScores = () => {
    if(data?.kingofthehill_game){
      let newTeamScores:Array<PlayerScore[]> = getTeamScores(data?.kingofthehill_game?.players || []);
      setTeamScores(newTeamScores);
    }
  }

  useEffect(()=>{
    updateScores();
  },[data?.kingofthehill_game,data?.kingofthehill_game?.deaths?.length]);

  const matches = useMediaQuery(`(max-width:${breakPoint}px)`);

  let characterClass = data?.kingofthehill_game?.character?.class;

  return (
    <React.Fragment>
      {data?.kingofthehill_game &&
        <section className={"game-container"} style={{
          gridTemplateAreas: matches ?  `
                                          'header header'
                                          'content content'
                                          'sidebar sidebar'
                                        ` 
                                        : `
                                            'header header'
                                            'content sidebar'
                                          `,
        }}>
          <div className="game-information">
            <GameHeader map={map?.name} zone={zone?.name} mode={data.kingofthehill_game?.mode} 
              endCondition={data.kingofthehill_game?.endCondition} time={data.kingofthehill_game?.time}
              scoreToWin={data.kingofthehill_game?.scoreToWin} gameTimer={hhmmss(data.kingofthehill_game?.timeElapsed || 0)} teamMMR={data.kingofthehill_game?.teamMMR} teamCMMR={data.kingofthehill_game?.teamCMMR}
              actions={(data.kingofthehill_game as any)?.tournament ? <TournamentLink tournament={(data.kingofthehill_game as any)?.tournament} /> : undefined}
            />
            {data.kingofthehill_game?.winner != null &&
              <WinnerBanner team={data.kingofthehill_game?.winner} />
            }
          </div>
          <section className="game-content">
            <div className="game-score">
              <GameScoreTabs game={data.kingofthehill_game}><GameScore teamScores={teamScores} game={data.kingofthehill_game} /></GameScoreTabs>
            </div>
            <RecordVideos 
              entries={(data.kingofthehill_game.players || []).map((p: any) => ({user: p.user, name: p.name || p.character?.name, video: p.video}))} />
          </section>
          <div className="game-sidebar">
            <div className="summary-information">
            <DeathBreakdown deaths={data.kingofthehill_game?.deaths} players={data.kingofthehill_game?.players} style={{maxHeight:'75vh'}} />
            </div>
          </div>
          <ReactResizeDetector handleWidth handleHeight targetDomEl={window.document.body} onResize={(width,height) => {
            if(containterWidth != width)
              setIsResizing(true);
            setContainerWidth(width);
          }} />
          <div
            className={classes.background}
            style={{ backgroundImage: "url(" + MapThumbnailIcons[data.kingofthehill_game.zone || ""][data.kingofthehill_game.map || ""] + ")" }}
          />
        </section>
      }
    </React.Fragment>
    
  )
}